import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import AuthContext from "../../contexts/AuthContext";
import GridItem from "./GridItem";
import FieldHeader from "./FieldHeader";
import CardText from "./CardText";
import ViewDocumentCard from "./ViewDocumentCard";
import ViewOtherDocuments from "./ViewOtherDocuments";
import ApprovalModal from "./Initiator/ApprovalModal";
import RejectModal from "./Initiator/RejectModal";
import ConfirmOtpModal from "../common/ConfirmOtp";
import { REQUESTREJECTIONREASONS } from "../../utils/constants";
import {
	getRequest,
	approveAuthorizerKycUpdateRequest,
	rejectAuthorizerKycUpdateRequest,
	sendOtp,
	verifyOtp,
	ixtracKYCUpdates,
	uploadHolderDocuments,
} from "../../services/admin";
import { newDateFormatter, getBase64ImageFromUrl } from "../../utils/helpers";

const ViewCorporateRequestUpdate = () => {
	const [openApproveModal, setOpenApproveModal] = useState(false);
	const [openRejectModal, setOpenRejectModal] = useState(false);
	const [checkedState, setCheckedState] = useState(
		new Array(REQUESTREJECTIONREASONS.length).fill(false)
	);
	const [otherReason, setotherReason] = useState("");
	const [openOtpModal, setOpenOtpModal] = useState(false);
	const [otp, setOtp] = useState("");

	const queryClient = useQueryClient();

	const { user } = useContext(AuthContext);

	const { id } = useParams();

	const {
		data: request = {},
		isLoading: requestLoading,
		isError: requestError,
		error: requestErrorMessage,
	} = useQuery(["get-request", id], async () => getRequest(id), {
		select: (data) => data.data,
		staleTime: 4 * 60 * 1000,
	});

	useEffect(() => {
		if (requestError) {
			if (requestErrorMessage.response) {
				toast.error(requestErrorMessage.response.data?.message);
			} else if (requestErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", requestErrorMessage.message);
			}
		}
	}, [requestError, requestErrorMessage]);

	const { mutate: approveUpdateRequest, isLoading: approveLoading } =
		useMutation("approve-kycUpdate", approveAuthorizerKycUpdateRequest, {
			onSuccess: () => {
				setOpenApproveModal(false);
				toast.success("KYC Update Request Approved Successfully");
			},
			onError: (errors) => {
				if (errors.response) {
					toast.error(errors.response.data.message);
				} else if (errors.request) {
					toast.error("Internal Server Error");
				} else {
					toast.error("Error", errors.message);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries("get-request");
			},
		});

	const { mutate: rejectUpdateRequest, isLoading: rejectLoading } = useMutation(
		"reject-kycUpdate",
		rejectAuthorizerKycUpdateRequest,
		{
			onSuccess: () => {
				setOpenRejectModal(false);
				toast.success("KYC Update Request Rejected Successfully");
			},
			onError: (errors) => {
				if (errors.response) {
					toast.error(errors.response.data.message);
				} else if (errors.request) {
					toast.error("Internal Server Error");
				} else {
					toast.error("Error", errors.message);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries("get-request");
			},
		}
	);

	const { mutate: sendOtpRequest } = useMutation("send-otp", sendOtp, {
		onSuccess: (dataSuccess) => {
			setOpenOtpModal(true);
			toast.success(dataSuccess?.message);
		},
		onError: (errors) => {
			if (errors.response) {
				toast.error(errors.response.data.message);
			} else if (errors.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", errors.message);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries("get-request");
		},
	});

	const { mutate: verifyOtpRequest, isLoading: verifyOtploading } = useMutation(
		"verify-otp",
		verifyOtp,
		{
			onSuccess: (dataSuccess) => {
				toast.success(dataSuccess?.message);
				setOtp("");
				setOpenOtpModal(false);
				handleUpdateIxtracKyc();
			},
			onError: (errors) => {
				if (errors.response) {
					toast.error(errors.response.data.message);
				} else if (errors.request) {
					toast.error("Internal Server Error");
				} else {
					toast.error("Error", errors.message);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries("get-request");
			},
		}
	);

	const { mutate: updateIxtracKyc, isLoading: updateIxtracKycLoading } =
		useMutation("ixtrac-kyc-update", ixtracKYCUpdates, {
			onSuccess: (dataSuccess) => {
				toast.success(dataSuccess);
				handleDocumentsUpload();
			},
			onError: (errors) => {
				if (errors.response) {
					toast.error(errors.response.data.message);
				} else if (errors.request) {
					toast.error("Internal Server Error");
				} else {
					toast.error("Error", errors.message);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries("get-request");
			},
		});

	const {
		mutate: updateHolderDocuments,
		isLoading: updateHolderDocumentsLoading,
	} = useMutation("upload-holder-documents", uploadHolderDocuments, {
		onSuccess: (dataSuccess) => {
			toast.success(dataSuccess?.message);
		},
		onError: (errors) => {
			if (errors.response) {
				toast.error(errors.response.data.message);
			} else if (errors.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", errors.message);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries("get-request");
		},
	});

	const handleVerifyOtp = async () => {
		const payload = {
			otp,
			CustID: request.custId,
			emailAddress: user.email,
		};
		await verifyOtpRequest(payload);
	};

	const handleUpdateIxtracKyc = async () => {
		const payload = {
			lastName: request.lastname,
			admin: user?.fullName,
			firstName: request.firstname,
			otherNames: request.middlename,
			Title: request.title,
			gender:
				request.accountType === "corporate" && request.gender === "F"
					? null
					: request.gender,
			address: request.addressLineOne,
			city: request.city,
			state: request.state,
			phoneNumber: request.phone,
			emailAddress: request.email,
			HolderTypeId: request.holderTypeId,
			dateOfBirth: request.dateOfBirth,
			nextOfKin: request.nextOfKin,
			BVNNumber: request.bankBVN,
			taxID: request.taxID,
			Maiden: request.motherMaidenName,
			CscsNumber: request.chn,
			OldAccountNumber: request.custId,
			CustID: request.custId,
			AltPhone: request.secondaryPhone,
			Address2: request.addressLineTwo,
			NIN: request.nin,
			NoTax: request.noTax,
			SearchParam: request.custId,
			searchOption: "O",
		};

		await updateIxtracKyc(payload);
	};

	const handleDocumentsUpload = async () => {
		const imagesArray = [
			request.passportOne,
			request.signatureOne,
			request.identificationOne,
			request.otherDocuments,
			"",
			null,
		];

		const mapped64ImagesPromises = imagesArray
			.filter((image) => image)
			.map(async (image) => {
				const result = await getBase64ImageFromUrl(image);
				return result;
			});

		const base64ImagesArray = await Promise.all(mapped64ImagesPromises);

		const trimed64Images = base64ImagesArray.map(
			(image) => image.split(",")[1]
		);

		const payload = trimed64Images.map((image) => ({
			accountNumber: request.accountNumber,
			registerID: request.registerId,
			CustId: request.custId || "",
			docTypeID: "1008",
			description: "KYC DOCUMENTS",
			admin: user?.fullName,
			image,
		}));

		await Promise.all(
			payload.map(async (image) => updateHolderDocuments(image))
		);
		await handleRequestApprove();
	};

	const handleRequestApprove = async () => {
		const payload = {
			id,
			authorizer: user?.fullName,
		};
		await approveUpdateRequest(payload);
	};

	const handleRejectRequest = async () => {
		if (!checkedState.some((item) => item)) {
			toast.error("Please choose a rejection reason");
			return;
		}
		const rejected = checkedState
			.map((item, index) => (item ? REQUESTREJECTIONREASONS[index] : false))
			.filter((item) => item !== false);
		const index = rejected.indexOf("Specify any other reason");
		const rejectedReasons = rejected;
		if (index !== -1) {
			rejectedReasons[index] = otherReason;
			if (!otherReason) {
				toast.error(
					"Kindly type in other reason for rejection or uncheck other reason checkbox"
				);
				return;
			}
		}
		const payload = {
			id,
			rejectReasons: rejectedReasons,
			initiator: user?.fullName,
		};

		await rejectUpdateRequest(payload);
	};

	const handleOnChange = (position) => {
		const updatedCheckedState = checkedState.map((item, index) =>
			index === position ? !item : item
		);
		setCheckedState(updatedCheckedState);
	};

	const handleOtpRequest = async () => {
		const payload = {
			fullName: user?.fullName,
			phoneNumber: request.phone,
			CustID: request.custId,
			emailAddress: user.email,
			admin: user?.fullName,
			bizFrom: "CSRL",
			transactionType: "ADMIN",
		};
		await sendOtpRequest(payload);
	};

	const approvalLoading =
		verifyOtploading ||
		updateIxtracKycLoading ||
		updateHolderDocumentsLoading ||
		approveLoading;

	return (
		<Box
			sx={{
				padding: {
					md: "3rem",
					sm: "1.5rem",
					xs: "1rem",
				},
				pt: "6rem !important",
			}}
		>
			{requestLoading && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						width: "100%",
						height: "100vh",
						mt: "-6rem",
					}}
				>
					<CircularProgress />
				</Box>
			)}
			{!requestLoading && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Box sx={{ width: { xs: "100%", md: "75%", lg: "67%" } }}>
						<Box
							sx={{
								padding: { md: "1.5rem", xs: "1rem", textAlign: "center" },
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								borderRadius: "0.5rem",
								backgroundColor: "#f7fafc",
							}}
						>
							<Typography
								variant="h5"
								sx={{ mt: "1rem", fontWeight: "bold" }}
								gutterBottom
								component="div"
								color="primary"
							>
								{request.lastname
									? `${request.lastname || ""}, ${request.firstname || ""} ${
											request.middlename || ""
									  }`
									: request.lastname}
							</Typography>

							<Typography
								variant="body1"
								sx={{ fontWeight: "bold", color: "#a0aec0" }}
								component="div"
							>
								<LocationOnIcon sx={{ mb: "-0.4rem" }} />{" "}
								{request.addressLineOne || ""} {request.addressLineTwo || ""}
							</Typography>
							<Typography
								variant="body1"
								sx={{ fontWeight: "bold", color: "#a0aec0" }}
								gutterBottom
								component="div"
							>
								{request.city || ""} {request.state || ""},{" "}
								{request.country || ""}
							</Typography>

							<Typography
								variant="body1"
								sx={{ color: "#4a5568" }}
								component="div"
							>
								<WorkIcon sx={{ mb: "-0.4rem", color: "#a0aec0" }} />{" "}
								{request.occupation || ""} {request.employer || ""}
							</Typography>
							<Typography
								variant="body1"
								sx={{ color: "#4a5568" }}
								gutterBottom
								component="div"
							>
								{request.employerAddress || ""}
							</Typography>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Basic Details" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.accountType}
												subHeader="account type"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.lastname?.toUpperCase()}
												subHeader="company name"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.email?.toUpperCase()}
												subHeader="email"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText header={request.phone} subHeader="phone" />
										</GridItem>
										<GridItem>
											<CardText
												header={request.secondaryPhone}
												subHeader="secondary phone"
											/>
										</GridItem>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Address Info" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.addressLineOne}
												subHeader="address line 1"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.addressLineTwo}
												subHeader="address line 2"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.city}
												subHeader="address city"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.state}
												subHeader="address state"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.country}
												subHeader="address country"
											/>
										</Grid>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Identification 1" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.identityTypeOne}
												subHeader="Means of Identification"
											/>
										</GridItem>
										{Boolean(request.enterIdentityTypeOne) && (
											<GridItem>
												<CardText
													header={request.enterIdentityTypeOne}
													subHeader="Identity Type"
												/>
											</GridItem>
										)}
									</Grid>
								</Box>

								<Box>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.identityNumberOne}
												subHeader="ID Number"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={newDateFormatter(request.identityExpiryDateOne)}
												subHeader="Expiry Date"
											/>
										</GridItem>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Identification 2" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.identityTypeTwo}
												subHeader="Means of Identification"
											/>
										</GridItem>
										{Boolean(request.enterIdentityTypeTwo) && (
											<GridItem>
												<CardText
													header={request.enterIdentityTypeTwo}
													subHeader="Identity Type"
												/>
											</GridItem>
										)}
									</Grid>
								</Box>

								<Box>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.identityNumberTwo}
												subHeader="ID Number"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={newDateFormatter(request.identityExpiryDateTwo)}
												subHeader="Expiry Date"
											/>
										</GridItem>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader
								header="Documents 1"
								subHeader="Uploaded Signature mandate, Passport photograph, and Means of identification."
							/>
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<ViewDocumentCard
									image={request.passportOne}
									title="Passport 1"
								/>
								<ViewDocumentCard
									image={request.signatureOne}
									title="Signature Mandate 1"
								/>
								<ViewDocumentCard
									image={request.identificationOne}
									title="Identification Document 1"
								/>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader
								header="Documents 2"
								subHeader="Uploaded Signature mandate, Passport photograph, and Means of identification."
							/>
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<ViewDocumentCard
									image={request.passportTwo}
									title="Passport 2"
								/>
								<ViewDocumentCard
									image={request.signatureTwo}
									title="Signature Mandate 2"
								/>
								<ViewDocumentCard
									image={request.identificationTwo}
									title="Identification Document 2"
								/>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Other Documents" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								{request.otherDocuments ? (
									<ViewOtherDocuments
										image={request.otherDocuments}
										title="Other Documents"
									/>
								) : (
									"No other document uploaded."
								)}
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Approving Officers" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Typography
									variant="body1"
									sx={{
										color: "#718096",
										textTransform: "uppercase",
										fontWeight: 500,
										mb: "1rem",
									}}
								>
									001. APPROVING OFFICER
								</Typography>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText header={request.initiator} subHeader="Name" />
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={newDateFormatter(request.approvalDateOne)}
												subHeader="Approval Date"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.status}
												subHeader="Approval Status"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Divider
									style={{
										marginTop: "2rem",
										marginBottom: "2rem",
									}}
								/>

								<Typography
									variant="body1"
									sx={{
										color: "#718096",
										textTransform: "uppercase",
										fontWeight: 500,
										mb: "1rem",
									}}
								>
									002. APPROVING OFFICER
								</Typography>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText header={request.authorizer} subHeader="Name" />
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={newDateFormatter(request.approvalDateTwo)}
												subHeader="Approval Date"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.status}
												subHeader="Approval Status"
											/>
										</GridItem>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Approval Status" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									boxShadow:
										"0 1px 3px 0 rgb(0 0 0 / 10%), 0 1px 2px 0 rgb(0 0 0 / 6%)",
								}}
							>
								<Typography
									variant="body2"
									sx={{
										fontWeight: 400,
										mb: "1rem",
									}}
									gutterBottom
								>
									These details requires both approvals.
								</Typography>
								<Box sx={{ display: "flex", justifyContent: "space-between" }}>
									<Button
										variant="contained"
										color="success"
										onClick={() => setOpenApproveModal(true)}
										disabled={approvalLoading}
									>
										{approvalLoading ? (
											<CircularProgress size="1.5rem" />
										) : (
											"approve update"
										)}
									</Button>
									<Button
										variant="contained"
										color="error"
										onClick={() => setOpenRejectModal(true)}
									>
										reject update
									</Button>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			)}
			<ApprovalModal
				open={openApproveModal}
				handleClose={() => setOpenApproveModal(false)}
				title="Approve KYC Update Request"
				subtitle=""
				handleRequestApprove={() => {
					handleUpdateIxtracKyc();
					setOpenApproveModal(false);
				}}
				approveLoading={approveLoading}
			/>
			<RejectModal
				open={openRejectModal}
				handleClose={() => setOpenRejectModal(false)}
				title="Reject KYC Update Request"
				subtitle=""
				handleRejectRequest={handleRejectRequest}
				rejectLoading={rejectLoading}
				REQUESTREJECTIONREASONS={REQUESTREJECTIONREASONS}
				otherReason={otherReason}
				setotherReason={setotherReason}
				checkedState={checkedState}
				handleOnChange={handleOnChange}
			/>
			<ConfirmOtpModal
				open={openOtpModal}
				handleClose={() => setOpenOtpModal(false)}
				otp={otp}
				handleChange={(value) => setOtp(value)}
				handleOtpRequest={handleOtpRequest}
				handleVerifyOtp={() => {
					handleVerifyOtp();
					setOpenOtpModal(false);
				}}
			/>
		</Box>
	);
};

export default ViewCorporateRequestUpdate;
